import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import css from '@styled-system/css';
import { themeGet } from '@styled-system/theme-get';
import { useSelect, useMultipleSelection } from 'downshift';
import { transparentize } from 'polished';
import React, { useEffect } from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { border, color, compose, layout, margin, shadow, space } from 'styled-system';
import theme from '../theme';
import List from './List';
import ListItem from './ListItem';
import Checkbox from './Checkbox';
function boxStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const boxTypes = {
        large: {
            padding: '3',
            fontSize: '1'
        },
        medium: {
            padding: '2',
            fontSize: '0'
        },
        small: {
            padding: '1',
            fontSize: '12px'
        }
    };
    return css(boxTypes[boxSize]);
}
function wrapperStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const wrapperTypes = {
        large: {
            width: '250px'
        },
        medium: {
            width: '165px'
        },
        small: {
            width: '100px'
        }
    };
    return css(wrapperTypes[boxSize]);
}
function listItemStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const textTypes = {
        large: {
            fontSize: '1',
            padding: '3'
        },
        medium: {
            fontSize: '0',
            padding: '2'
        },
        small: {
            fontSize: '12px',
            padding: '1'
        }
    };
    return css(textTypes[boxSize]);
}
const StyledWrapper = styled.div `
  ${css({
    display: 'block'
})}
  ${wrapperStyles}
  ${compose(margin, layout)}
`;
function toggledButtonStyles(props) {
    if (!props.isOpen) {
        return;
    }
    return css({
        boxShadow: `0 0 1px 1px ${themeGet('colors.gray.3', 'lightgray')(props)}`,
        borderColor: 'gray.3',
        borderStyle: 'solid',
        borderWidth: '1',
        outline: '0'
    });
}
const StyledButton = styled.button `
  ${css({
    alignItems: 'center',
    bg: 'white',
    borderColor: 'gray.4',
    borderRadius: '1',
    borderStyle: 'solid',
    borderWidth: '1',
    color: 'gray.1',
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'body',
    fontWeight: 'light',
    px: '3',
    py: '2',
    width: '100%'
})}
  ${boxStyles}
  &:focus {
    ${props => toggledButtonStyles(Object.assign(Object.assign({}, props), { isOpen: true }))}
  }
  &:disabled {
    ${css({
    bg: 'gray.6',
    color: 'gray.4',
    cursor: 'not-allowed'
})}
  }
  ${toggledButtonStyles}
  ${compose(border, color, layout, space)}
`;
const StyledList = styled(List) `
  width: -moz-available;
  width: -webkit-fill-available;
  ${css({
    bg: 'white',
    borderColor: 'gray.5',
    borderRadius: '1',
    borderStyle: 'solid',
    borderWidth: '1',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    cursor: 'default',
    maxHeight: '180px',
    my: '1',
    overflow: 'auto',
    position: 'absolute'
})}
  &:focus {
    ${css({
    outline: '0'
})}
  }
  ${compose(border, color, layout, shadow, space)}
`;
const StyledListItem = styled(ListItem) `
  ${({ theme, isSelected, isHighlighted, multiple, color }) => {
    let selectedColor = color || 'brand';
    selectedColor = themeGet(`colors.${selectedColor}`, selectedColor)({ theme });
    let bgColor = isHighlighted ? transparentize(0.75, selectedColor) : '';
    bgColor = isSelected ? selectedColor : bgColor;
    if (multiple) {
        bgColor = isHighlighted ? 'gray.6' : '';
    }
    return css({
        bg: bgColor,
        color: isSelected && !multiple ? 'white' : ''
    });
}}
  ${listItemStyles}
`;
const StyledLabel = styled.span `
  ${({ hasSelected }) => css({
    color: !hasSelected ? 'gray.4' : '',
    mr: '4',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
})}
`;
const DownIcon = styled(FontAwesomeIcon) `
  ${({ disabled, color }) => css({
    color: !disabled ? color || 'brand' : 'gray.4',
    marginLeft: 'auto'
})}
`;
const NativeInput = styled.input `
  left: 0;
  width: 100%;
  bottom: 0;
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;
const Select = ({ options, defaultValue, value, multiple, placeholder, onChange = () => '', renderOption, id, name, disabled, color, drop, button, wrapper, boxSize, theme, autoFocus }) => {
    const { getDropdownProps, addSelectedItem, removeSelectedItem, selectedItems } = useMultipleSelection(multiple && value ? { initialSelectedItems: value } : {});
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onStateChange = ({ type, selectedItem }) => {
        switch (type) {
            case useSelect.stateChangeTypes.MenuKeyDownEnter:
            case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
            case useSelect.stateChangeTypes.ItemClick:
                if (selectedItem) {
                    if (selectedItems.indexOf(selectedItem) > -1) {
                        removeSelectedItem(selectedItem);
                    }
                    else {
                        addSelectedItem(selectedItem);
                    }
                }
                break;
            default:
                break;
        }
    };
    const stateReducer = (state, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        switch (type) {
            case useSelect.stateChangeTypes.MenuKeyDownEnter:
            case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
            case useSelect.stateChangeTypes.ItemClick:
                return Object.assign(Object.assign({}, changes), { isOpen: true // keep the menu open after selection.
                 });
        }
        return changes;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectProps = {
        defaultSelectedItem: defaultValue,
        items: options,
        onSelectedItemChange: !multiple
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (changes) => {
                    if (onChange && changes && changes.selectedItem) {
                        onChange(changes.selectedItem);
                    }
                }
            : undefined
    };
    if (multiple) {
        selectProps.stateReducer = stateReducer;
        // TODO: Typings
        // https://github.com/downshift-js/downshift/issues/1015
        selectProps.onStateChange = onStateChange;
        selectProps.selectedItem = null;
    }
    else {
        selectProps.selectedItem = value;
    }
    const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, selectedItem, getItemProps } = useSelect(selectProps);
    useEffect(() => {
        if (multiple) {
            onChange(selectedItems);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItems, multiple]);
    const renderLabels = () => {
        if (multiple) {
            return selectedItems.length > 0
                ? `${selectedItems.length} selected`
                : placeholder || 'Select...';
        }
        else {
            return selectedItem
                ? selectedItem.label
                : defaultValue
                    ? defaultValue.label
                    : placeholder || 'Select...';
        }
    };
    const [referenceElement, setReferenceElement] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start'
    });
    const menuProps = getMenuProps();
    const menuRef = (ref) => {
        setPopperElement(ref);
        menuProps.ref(ref);
    };
    return (React.createElement(StyledWrapper, Object.assign({ id: id, name: name, boxSize: boxSize }, wrapper),
        React.createElement("div", { ref: setReferenceElement },
            React.createElement(StyledButton, Object.assign({ isOpen: isOpen, disabled: disabled, boxSize: boxSize, theme: theme }, button, getToggleButtonProps(getDropdownProps({ preventKeyAction: isOpen }))),
                React.createElement(StyledLabel, { hasSelected: selectedItems.length > 0 || !!selectedItem }, renderLabels()),
                React.createElement(NativeInput, { value: selectedItems.map((x) => x.label).join(','), name: name, readOnly: true, "aria-hidden": true, tabIndex: -1, autoFocus: autoFocus }),
                React.createElement(DownIcon, { icon: faChevronDown, color: color, disabled: disabled, "aria-hidden": 'true', theme: theme }))),
        React.createElement("div", Object.assign({}, menuProps, { ref: menuRef, style: Object.assign(Object.assign(Object.assign({}, menuProps.style), styles.popper), { width: referenceElement ? referenceElement.clientWidth : undefined }) }, attributes.popper), isOpen && (React.createElement(StyledList, Object.assign({}, menuProps, drop), options.map((item, index) => (React.createElement(StyledListItem, Object.assign({ boxSize: boxSize, color: color, isHighlighted: highlightedIndex === index, multiple: multiple, isSelected: multiple
                ? selectedItems.indexOf(item) > -1
                : selectedItem &&
                    selectedItem.value === item.value, key: index }, getItemProps({ item, index })), multiple ? (React.createElement(Checkbox, { checked: selectedItems.indexOf(item) > -1, readOnly: true }, renderOption ? renderOption(item) : item.label)) : renderOption ? (renderOption(item)) : (item.label)))))))));
};
Select.defaultProps = {
    theme
};
export default Select;
//# sourceMappingURL=Select.js.map